import { Component, Inject, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { PosthogService } from '../../services/posthog.service';
import { HttpService } from '../../services/http-service';
import { ITranslation } from '../../models/translations/translations';

export interface VideoPlayerData {
  videoUrl: string;
  title?: string;
  useHtml5?: boolean; // If true, use HTML5 video player; if false, use YouTube iframe
}

export interface VideoWatchStats {
  maxWatchedPercentage: number;
  fullyWatched: boolean;
  startedPlaying: boolean;
}

@Component({
  selector: 'app-video-player-modal',
  templateUrl: './video-player-modal.component.html',
  styleUrls: ['./video-player-modal.component.scss']
})
export class VideoPlayerModalComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('videoPlayer', { static: false }) videoPlayer: ElementRef<HTMLVideoElement>;

  safeVideoUrl: SafeResourceUrl;
  videoUrl: string;
  title: string;
  useHtml5: boolean;
  private modalOpenTime: number;
  private videoStartTime: number = 0;
  private maxWatchedPercentage: number = 0;
  private hasStartedPlaying: boolean = false;
  private completionEventSent: boolean = false;

  get t(): ITranslation {
    return this.httpService.getTransation();
  }

  constructor(
    public dialogRef: MatDialogRef<VideoPlayerModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: VideoPlayerData,
    private sanitizer: DomSanitizer,
    private posthog: PosthogService,
    public httpService: HttpService
  ) {
    this.modalOpenTime = Date.now();
  }

  ngOnInit(): void {
    this.title = this.data.title || 'Tutorial Video';
    this.useHtml5 = this.data.useHtml5 !== undefined ? this.data.useHtml5 : false;

    // Track video modal opened
    this.posthog.captureEvent('video_tutorial_opened', {
      video_title: this.title,
      video_type: this.useHtml5 ? 'html5' : 'youtube',
      timestamp: new Date().toISOString()
    });

    if (this.useHtml5) {
      // For self-hosted videos, use the URL directly
      this.videoUrl = this.data.videoUrl;
    } else {
      // Convert YouTube watch URL to embed URL
      const embedUrl = this.convertToEmbedUrl(this.data.videoUrl);
      this.safeVideoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(embedUrl);
    }
  }

  ngAfterViewInit(): void {
    // Add event listeners for HTML5 video player
    if (this.useHtml5 && this.videoPlayer?.nativeElement) {
      const video = this.videoPlayer.nativeElement;

      video.addEventListener('play', () => {
        if (!this.hasStartedPlaying) {
          this.hasStartedPlaying = true;
          this.videoStartTime = Date.now();

          this.posthog.captureEvent('video_tutorial_started', {
            video_title: this.title,
            time_to_play_ms: Date.now() - this.modalOpenTime,
            timestamp: new Date().toISOString()
          });
        }
      });

      video.addEventListener('timeupdate', () => {
        if (video.duration > 0) {
          const percentage = (video.currentTime / video.duration) * 100;
          if (percentage > this.maxWatchedPercentage) {
            this.maxWatchedPercentage = percentage;
          }
        }
      });

      video.addEventListener('ended', () => {
        this.posthog.captureEvent('video_tutorial_completed', {
          video_title: this.title,
          watch_duration_ms: Date.now() - this.videoStartTime,
          timestamp: new Date().toISOString()
        });
        this.completionEventSent = true;
      });
    }
  }

  ngOnDestroy(): void {
    if (
      this.useHtml5 &&
      this.hasStartedPlaying &&
      !this.completionEventSent &&
      this.maxWatchedPercentage >= 80
    ) {
      this.posthog.captureEvent('video_tutorial_completed', {
        video_title: this.title,
        watch_duration_ms: Date.now() - this.videoStartTime,
        timestamp: new Date().toISOString(),
        completion_reason: 'percentage_threshold',
        watched_percentage: Math.round(this.maxWatchedPercentage)
      });
      this.completionEventSent = true;
    }

    // Track video close with watch statistics
    const timeOpen = Date.now() - this.modalOpenTime;

    this.posthog.captureEvent('video_tutorial_closed', {
      video_title: this.title,
      time_open_ms: timeOpen,
      max_watched_percentage: Math.round(this.maxWatchedPercentage),
      fully_watched: this.maxWatchedPercentage >= 95,
      started_playing: this.hasStartedPlaying,
      timestamp: new Date().toISOString()
    });
  }

  public getWatchStats(): VideoWatchStats {
    return {
      maxWatchedPercentage: Math.round(this.maxWatchedPercentage),
      fullyWatched: this.maxWatchedPercentage >= 95,
      startedPlaying: this.hasStartedPlaying,
    };
  }

  private convertToEmbedUrl(url: string): string {
    // Extract video ID from various YouTube URL formats
    let videoId = '';

    // Handle youtube.com/watch?v=VIDEO_ID format
    const watchMatch = url.match(/[?&]v=([^&]+)/);
    if (watchMatch) {
      videoId = watchMatch[1];
    }

    // Handle youtu.be/VIDEO_ID format
    const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
    if (shortMatch) {
      videoId = shortMatch[1];
    }

    // Handle youtube.com/embed/VIDEO_ID format
    const embedMatch = url.match(/youtube\.com\/embed\/([^?&]+)/);
    if (embedMatch) {
      videoId = embedMatch[1];
    }

    // Return embed URL with autoplay
    return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
  }

  onClose(): void {
    // Pause HTML5 video if it's playing to prevent audio continuing after modal closes
    if (this.useHtml5 && this.videoPlayer?.nativeElement) {
      try {
        this.videoPlayer.nativeElement.pause();
      } catch (error) {
        // Silently ignore errors when pausing video
        console.warn('Error pausing video on close:', error);
      }
    }
    this.dialogRef.close();
  }
}
